import { useCallback, useMemo } from 'react';
import { Message, UnreadConversation } from 'talkjs/types/talk.types';
import { fetchy } from '@bridebook/web/lib/fetchy';
import { Conversation } from '@bridebook/toolbox/src/inbox/api/talkjs/webhook.types';
import { ConversationOptions } from '@bridebook/toolbox/src/inbox/types';
import {
  mapTalkJsClientMessageToAPIMessage,
  isArchivedByCouple,
  isMessageCenterConversation,
  isSupplierRole,
} from '@bridebook/toolbox/src/inbox/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useInboxContext } from '@bridebook/web/components/inbox/context/inbox-context';
import { toMillis, useSelector } from '@bridebook/web/lib/utils';
import { getWeddingProfileId } from '@bridebook/web/lib/weddings/selectors';
import { InboxView } from '@bridebook/web/components/inbox/context/types';

export const USE_CONVERSATIONS_QUERY_KEY = ['inbox/conversations'];

/**
 * Fetches the wedding's inbox conversations, for the query below and for callers
 * that need the list fresh.
 */
export const fetchConversationsByWeddingId = (weddingId: string) =>
  fetchy().authenticated.get('/api/inbox/conversations/[weddingId]', { weddingId });

const CACHE_TIME = toMillis(1, 'hour');

interface Props {
  /**
   * Optional filter to include only conversations from a specific view
   */
  filterByView?: InboxView;
}

/**
 * Fetches and manages the list of conversations for the inbox.
 */
export const useConversations = ({ filterByView }: Props = {}) => {
  const weddingId = useSelector(getWeddingProfileId);
  const { conversationId } = useInboxContext();
  const queryClient = useQueryClient();

  const { data, isLoading, isRefetching } = useQuery(
    USE_CONVERSATIONS_QUERY_KEY,
    () => fetchConversationsByWeddingId(weddingId),
    {
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      refetchOnWindowFocus: false,
      select: sortConversationsByLatestActivity,
    },
  );

  /**
   * Updates the cache locally when a new message arrives, so we don't need to refetch the whole list
   */
  const onNewMessage = useCallback(
    (message: Message) => {
      queryClient.setQueryData<Conversation[]>(USE_CONVERSATIONS_QUERY_KEY, (list = []) => {
        const existingConversation = list.find((c) => c.id === message.conversation.id);
        if (!existingConversation) {
          queryClient.invalidateQueries(USE_CONVERSATIONS_QUERY_KEY);
          return list;
        }

        const isFromSupplier = isSupplierRole(message.sender?.role);

        const updatedConversation: Conversation = {
          ...existingConversation,
          // Mark conversation as unread if it's not the selected conversation
          isUnread: conversationId !== message.conversation.id,
          lastMessage: {
            // Spreading existing conversation to avoid re-rendering if there are any missing props in the mapper
            ...existingConversation.lastMessage,
            ...mapTalkJsClientMessageToAPIMessage(message),
          },
          ...(isFromSupplier && {
            custom: {
              ...existingConversation.custom,
              repliedBySupplier:
                existingConversation.custom?.repliedBySupplier || String(Date.now()),
            } as ConversationOptions['custom'],
          }),
        };

        return [updatedConversation, ...list.filter((c) => c.id !== message.conversation.id)];
      });
    },
    [conversationId, queryClient],
  );

  /**
   * Sets `isUnread` property to false,
   * so we can hide the unread indicator without refetching the whole list
   */
  const markAsRead = useCallback(
    (selectedId: string) => {
      queryClient.setQueryData<Conversation[]>(USE_CONVERSATIONS_QUERY_KEY, (list = []) =>
        list.map((c) =>
          c.id === selectedId ? { ...c, isUnread: false, unreadMessageCount: 0 } : c,
        ),
      );
    },
    [queryClient],
  );

  /**
   * Sets `repliedByCouple` property when a message is sent,
   * so we can hide Quick Responses without refetching the whole list
   */
  const markAsReplied = useCallback(
    (selectedId: string) => {
      queryClient.setQueryData<Conversation[]>(USE_CONVERSATIONS_QUERY_KEY, (list = []) =>
        list.map((c) =>
          c.id === selectedId
            ? {
                ...c,
                custom: {
                  ...c.custom,
                  repliedByCouple: String(Date.now()),
                } as ConversationOptions['custom'],
              }
            : c,
        ),
      );
    },
    [queryClient],
  );

  const onUnreadChange = useCallback(
    (unreadConversations: UnreadConversation[]) => {
      queryClient.setQueryData<Conversation[]>(USE_CONVERSATIONS_QUERY_KEY, (list = []) => {
        const conversationsToUpdateMap = new Map(
          unreadConversations.map((uc) => [uc.conversation.id, uc.unreadMessageCount]),
        );
        return list.map((conversation) => {
          if (!conversationsToUpdateMap.has(conversation.id)) return conversation;

          const newUnreadMessageCount =
            conversationsToUpdateMap.get(conversation.id) ?? conversation.unreadMessageCount;
          return { ...conversation, unreadMessageCount: newUnreadMessageCount };
        });
      });
    },
    [queryClient],
  );

  const conversations = useMemo(() => {
    if (!data) return;

    switch (filterByView) {
      case InboxView.Suppliers:
        return data.filter((c) => !isArchivedByCouple(c) && !isMessageCenterConversation(c));
      case InboxView.Archived:
        return data.filter((c) => isArchivedByCouple(c) && !isMessageCenterConversation(c));
      case InboxView.MessageCenter:
        return data.filter(isMessageCenterConversation);
      default:
        return data;
    }
  }, [data, filterByView]);

  const hasUnreadConversations = useMemo(
    () => !!conversations?.find((c) => c.isUnread && c.unreadMessageCount > 0),
    [conversations],
  );

  const currentConversation = useMemo(
    () => conversations?.find((c) => c.id === conversationId),
    [conversations, conversationId],
  );

  return {
    conversations,
    currentConversation,
    hasUnreadConversations,
    isLoading,
    isRefetching,
    onNewMessage,
    markAsRead,
    markAsReplied,
    onUnreadChange,
  };
};

export const invalidateConversationsQuery = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries(USE_CONVERSATIONS_QUERY_KEY);

// conversations sometimes get pushed to the bottom despite the implied sorting by activity time
const sortConversationsByLatestActivity = (conversations: Conversation[]) =>
  conversations.sort(
    (a: Conversation, b: Conversation) =>
      // lastMessage can be null in some cases
      (b.lastMessage?.createdAt || 0) - (a.lastMessage?.createdAt || 0),
  );
